import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { employerAPI } from '../services/api';
import { adminAssessmentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to get proxied URL for job files from S3
const getProxiedFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  
  // If it's already a proxy URL, return as-is
  if (fileUrl.startsWith(`${API_BASE_URL}/jobs/files/`)) {
    return fileUrl;
  }
  
  // If it's an HTTP/HTTPS URL, try to extract S3 key
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    try {
      const url = new URL(fileUrl);
      let s3Key = url.pathname;
      
      // Remove leading slash
      if (s3Key.startsWith('/')) {
        s3Key = s3Key.substring(1);
      }
      
      // Remove query parameters
      s3Key = s3Key.split('?')[0];
      
      // Handle different S3 URL formats:
      // 1. Standard AWS: https://bucket.s3.region.amazonaws.com/jd/{job_id}/{filename}
      // 2. Path-style: https://endpoint/bucket/jd/{job_id}/{filename}
      // 3. Virtual-host: https://endpoint/jd/{job_id}/{filename}
      
      // If path starts with bucket name (path-style), remove it
      // Common bucket names might be in the path, but we'll assume the S3 key starts with 'jd/'
      if (s3Key && s3Key.startsWith('jd/')) {
        // Use proxy pattern for job files: /jobs/files/{s3Key}
        // S3 key should be in format: jd/{job_id}/{filename}
        return `${API_BASE_URL}/jobs/files/${s3Key}`;
      }
      
      // If it doesn't start with 'jd/', it might be a different format
      // Try to find 'jd/' in the path
      const jdIndex = s3Key.indexOf('jd/');
      if (jdIndex !== -1) {
        const extractedKey = s3Key.substring(jdIndex);
        return `${API_BASE_URL}/jobs/files/${extractedKey}`;
      }
    } catch (e) {
      console.error('Error parsing file URL:', e);
    }
    // For non-S3 URLs or if parsing fails, return original URL
    return fileUrl;
  }
  
  // If it's a relative path, prepend API base URL
  if (fileUrl.startsWith('/')) {
    return `${API_BASE_URL}${fileUrl}`;
  }
  
  // Return as-is for other cases
  return fileUrl;
};

const JobPosting = ({ job, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const isEditMode = !!job;
  
  // Prepare initial values function
  const getInitialValues = useCallback(() => {
    if (!job) {
      return {
        jobTitle: '',
        jobDescription: '',
        location: '',
        validUntil: '',
        skills: '',
        email: user?.email || ''
      };
    }

    // Parse date - handle both camelCase and snake_case, and various date formats
    let validUntilDate = '';
    const deadline = job.applicationDeadline || job.application_deadline;
    if (deadline) {
      try {
        const date = new Date(deadline);
        if (!isNaN(date.getTime())) {
          validUntilDate = date.toISOString().split('T')[0];
        }
      } catch (e) {
        console.error('Error parsing date:', e);
      }
    }

    // Parse skills - handle both array and string formats
    let skillsValue = '';
    const skills = job.requiredSkills || job.required_skills;
    if (skills) {
      if (Array.isArray(skills)) {
        skillsValue = skills.join(', ');
      } else if (typeof skills === 'string') {
        skillsValue = skills;
      }
    }

    // Get email
    const email = job.applicationEmail || job.application_email || '';

    // Get location - handle both string and array formats
    const location = job.location || '';
    let initialLocations = [];
    let initialOther = '';
    
    if (location) {
      if (typeof location === 'string') {
        // Check if it contains predefined cities or is "Other"
        const locationParts = location.split(',').map(l => l.trim()).filter(l => l);
        const predefinedCities = getIndianITCities();
        locationParts.forEach(part => {
          if (predefinedCities.includes(part)) {
            initialLocations.push(part);
          } else if (part.toLowerCase().startsWith('other:')) {
            initialLocations.push('Other');
            initialOther = part.replace(/^other:\s*/i, '');
          } else if (!predefinedCities.includes(part)) {
            // If it's not a predefined city, treat as "Other"
            initialLocations.push('Other');
            initialOther = part;
          }
        });
      } else if (Array.isArray(location)) {
        location.forEach(loc => {
          if (typeof loc === 'string') {
            if (loc.toLowerCase().startsWith('other:')) {
              initialLocations.push('Other');
              initialOther = loc.replace(/^other:\s*/i, '');
            } else {
              initialLocations.push(loc);
            }
          }
        });
      }
    }

    return {
      jobTitle: job.title || '',
      jobDescription: job.description || '',
      location: location,
      validUntil: validUntilDate,
      skills: skillsValue,
      email: email,
      initialLocations: initialLocations,
      initialOther: initialOther
    };
  }, [job, user]);

  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm({
    defaultValues: getInitialValues()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [isEmailDropdownOpen, setIsEmailDropdownOpen] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [otherLocation, setOtherLocation] = useState('');

  const validUntil = watch('validUntil');
  const location = watch('location');

  // Fetch employees for the current employer
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const response = await employerAPI.getEmployees();
        const employeeList = response.data || [];
        setEmployees(employeeList);
        
        // If editing and job has email, try to match with employees
        if (isEditMode && job) {
          const email = job.applicationEmail || job.application_email || '';
          if (email) {
            // Handle comma-separated emails
            const emailList = email.split(',').map(e => e.trim()).filter(e => e);
            const matchedEmails = emailList.filter(e => 
              employeeList.some(emp => emp.email === e)
            );
            if (matchedEmails.length > 0) {
              setSelectedEmails(matchedEmails);
              setValue('email', matchedEmails.join(', '));
            } else {
              // If no employees match, set the email value directly (for manual entry fallback)
              setValue('email', email);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load employees:', error);
        // Don't show error toast, just log it - employees are optional
      } finally {
        setLoadingEmployees(false);
      }
    };
    
    fetchEmployees();
  }, [isEditMode, job, setValue]);

  // Initialize form when job prop changes
  useEffect(() => {
    const formValues = getInitialValues();
    reset(formValues);
    if (formValues.initialLocations) {
      setSelectedLocations(formValues.initialLocations);
    }
    if (formValues.initialOther) {
      setOtherLocation(formValues.initialOther);
    }
  }, [job, reset, getInitialValues]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setUploadedFile(file);
      
      // Show preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFilePreview(null);
    setValue('uploadFile', null);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Prepare location string from selected locations
      let locationString = '';
      if (selectedLocations.length > 0) {
        const locationParts = selectedLocations.map(loc => {
          if (loc === 'Other' && otherLocation.trim()) {
            return `Other: ${otherLocation.trim()}`;
          }
          return loc;
        }).filter(loc => loc !== 'Other' || otherLocation.trim()); // Remove "Other" if no value provided
        
        locationString = locationParts.join(', ');
      }
      
      if (!locationString) {
        toast.error('Please select at least one location');
        setIsSubmitting(false);
        return;
      }
      
      // Prepare form data for file upload
      const formData = new FormData();
      formData.append('jobTitle', data.jobTitle);
      formData.append('jobDescription', data.jobDescription);
      formData.append('location', locationString);
      formData.append('validUntil', data.validUntil);
      formData.append('skills', data.skills);
      formData.append('email', data.email);
      
      // Add file if uploaded
      if (uploadedFile) {
        formData.append('uploadFile', uploadedFile);
      }

      // Create or update job posting
      let response;
      if (isEditMode && job?.id) {
        response = await employerAPI.updateJobPosting(job.id, formData);
        toast.success('Job posting updated successfully!');
      } else {
        response = await employerAPI.createJobPosting(formData);
        toast.success('Job posting created successfully!');
      }
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} job posting:`, error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          `Failed to ${isEditMode ? 'update' : 'create'} job posting. Please try again.`;
      
      // Handle validation errors
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const validationErrors = error.response.data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        toast.error(validationErrors);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="">

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Job Title */}
        <div>
          <label htmlFor="jobTitle" className="form-label">
            Job Title <span className="text-red-500">*</span>
          </label>
          <input
            id="jobTitle"
            type="text"
            className={`input-field ${errors.jobTitle ? 'border-red-500' : ''}`}
            placeholder="e.g., Senior Software Engineer"
            {...register('jobTitle', {
              required: 'Job title is required',
              minLength: {
                value: 3,
                message: 'Job title must be at least 3 characters'
              }
            })}
          />
          {errors.jobTitle && (
            <p className="mt-1 text-sm text-red-600">{errors.jobTitle.message}</p>
          )}
        </div>

        {/* Job Description */}
        <div>
          <label htmlFor="jobDescription" className="form-label">
            Job Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="jobDescription"
            rows={6}
            className={`input-field ${errors.jobDescription ? 'border-red-500' : ''}`}
            placeholder="Describe the role, responsibilities, requirements, and benefits..."
            {...register('jobDescription', {
              required: 'Job description is required',
              minLength: {
                value: 50,
                message: 'Job description must be at least 50 characters'
              }
            })}
          />
          {errors.jobDescription && (
            <p className="mt-1 text-sm text-red-600">{errors.jobDescription.message}</p>
          )}
        </div>

        {/* Location - Multi-select with Indian IT Cities */}
        <LocationMultiSelect
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          otherLocation={otherLocation}
          setOtherLocation={setOtherLocation}
          error={errors.location}
          setValue={setValue}
          register={register}
        />

        {/* File Upload */}
        <div>
          <label htmlFor="uploadFile" className="form-label">
            Additional Documents (Optional)
          </label>
          {isEditMode && job?.tags && typeof job.tags === 'object' && job.tags.upload_file_url && !uploadedFile && (
            <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-sm text-blue-700">Current document: </span>
                  <a
                    href={getProxiedFileUrl(job.tags.upload_file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View existing document
                  </a>
                </div>
                <span className="text-xs text-blue-600">Upload a new file to replace</span>
              </div>
            </div>
          )}
          <div className="mt-1">
            {!uploadedFile ? (
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="uploadFile"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-10 h-10 mb-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX, or Images (MAX. 10MB)</p>
                  </div>
                  <input
                    id="uploadFile"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  {filePreview ? (
                    <img src={filePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-primary-100 rounded flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                    <p className="text-xs text-gray-500">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-red-600 hover:text-red-700 p-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Valid Until Date */}
        <div>
          <label htmlFor="validUntil" className="form-label">
            Application Valid Until <span className="text-red-500">*</span>
          </label>
          <input
            id="validUntil"
            type="date"
            className={`input-field ${errors.validUntil ? 'border-red-500' : ''}`}
            min={new Date().toISOString().split('T')[0]}
            {...register('validUntil', {
              required: 'Valid until date is required',
              validate: (value) => {
                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (selectedDate < today) {
                  return 'Date must be today or in the future';
                }
                return true;
              }
            })}
          />
          {errors.validUntil && (
            <p className="mt-1 text-sm text-red-600">{errors.validUntil.message}</p>
          )}
        </div>

        {/* Skills - Multi-select Dropdown */}
        <SkillsMultiSelect
          value={watch('skills')}
          onChange={(value) => setValue('skills', value, { shouldValidate: true })}
          error={errors.skills}
          required
        />

        {/* Contact Email - Multi-select from Employees */}
        <div>
          <label htmlFor="email" className="form-label">
            Contact Email <span className="text-red-500">*</span>
          </label>
          {loadingEmployees ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading employees...
            </div>
          ) : employees.length === 0 ? (
            <div className="space-y-2">
              <input
                id="email"
                type="text"
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                placeholder="hr@company.com (or multiple emails separated by commas)"
                {...register('email', {
                  required: 'Contact email is required',
                  validate: (value) => {
                    if (!value) return 'Contact email is required';
                    // Validate each email if comma-separated
                    const emails = value.split(',').map(e => e.trim()).filter(e => e);
                    if (emails.length === 0) return 'At least one email is required';
                    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                    const invalidEmails = emails.filter(e => !emailRegex.test(e));
                    if (invalidEmails.length > 0) {
                      return `Invalid email(s): ${invalidEmails.join(', ')}`;
                    }
                    return true;
                  }
                })}
              />
              <p className="text-xs text-gray-500">No employees found. Enter email(s) manually or add employees first. Separate multiple emails with commas.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Custom Multi-Select Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsEmailDropdownOpen(!isEmailDropdownOpen)}
                  className={`w-full px-4 py-2.5 text-left bg-white border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 flex items-center justify-between`}
                >
                  <span className={selectedEmails.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
                    {selectedEmails.length === 0 
                      ? 'Select employee(s) for contact email...' 
                      : `${selectedEmails.length} employee${selectedEmails.length > 1 ? 's' : ''} selected`}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isEmailDropdownOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isEmailDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsEmailDropdownOpen(false)}
                    ></div>
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {employees.map((employee) => {
                        const isSelected = selectedEmails.includes(employee.email);
                        const roleLabel = employee.role === 'hr_recruiter' ? 'HR Recruiter' : 
                                         employee.role === 'hr_manager' ? 'HR Manager' : 
                                         employee.role || '';
                        return (
                          <div
                            key={employee.id}
                            onClick={() => {
                              let updated;
                              if (isSelected) {
                                updated = selectedEmails.filter(e => e !== employee.email);
                              } else {
                                updated = [...selectedEmails, employee.email];
                              }
                              setSelectedEmails(updated);
                              setValue('email', updated.length > 0 ? updated.join(', ') : '', { shouldValidate: true });
                            }}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                              isSelected ? 'bg-primary-50 border-l-4 border-l-primary-600' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  isSelected 
                                    ? 'bg-primary-600 border-primary-600' 
                                    : 'border-gray-300'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {(() => {
                                      const firstName = employee.firstName || employee.first_name || '';
                                      const lastName = employee.lastName || employee.last_name || '';
                                      const fullName = `${firstName} ${lastName}`.trim();
                                      return fullName || employee.email || 'Employee';
                                    })()}
                                  </p>
                                  <p className="text-xs text-gray-600">{employee.email}</p>
                                  {roleLabel && (
                                    <p className="text-xs text-gray-500 mt-0.5">{roleLabel}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Selected Emails Display */}
              {selectedEmails.length > 0 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Selected Contact Emails:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedEmails.map((email) => {
                      const employee = employees.find(emp => emp.email === email);
                      const employeeName = employee 
                        ? `${employee.firstName || employee.first_name || ''} ${employee.lastName || employee.last_name || ''}`.trim()
                        : '';
                      return (
                        <span
                          key={email}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 text-primary-800 rounded-lg text-sm"
                        >
                          {employeeName ? (
                            <>
                              <span className="font-medium">{employeeName}</span>
                              <span className="text-primary-600">({email})</span>
                            </>
                          ) : (
                            <span className="font-medium">{email}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = selectedEmails.filter(e => e !== email);
                              setSelectedEmails(updated);
                              setValue('email', updated.length > 0 ? updated.join(', ') : '', { shouldValidate: true });
                            }}
                            className="text-primary-600 hover:text-primary-800 ml-1"
                            title="Remove"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hidden input for form validation */}
              <input
                type="hidden"
                {...register('email', {
                  required: 'At least one contact email is required',
                  validate: () => {
                    if (selectedEmails.length === 0) {
                      return 'Please select at least one employee email';
                    }
                    return true;
                  }
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          )}
        </div>

        {/* Auto-filled fields info - only show in create mode */}
        {!isEditMode && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> The following information will be automatically added:
            </p>
            <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Posted on: {new Date().toLocaleDateString()}</li>
              <li>Created by: {user?.firstName || ''} {user?.lastName || ''} ({user?.email || ''})</li>
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </span>
            ) : (
              isEditMode ? 'Update Job Posting' : 'Create Job Posting'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Skills Multi-Select Component - Simple dropdown with checkboxes
const SkillsMultiSelect = ({ value, onChange, error, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [allSkills, setAllSkills] = useState([]);
  const dropdownRef = useRef(null);

  // Parse selected skills from value
  const selectedSkills = value 
    ? (typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(s => s) : [])
    : [];

  // Load all skills on mount - get from database endpoint first, then fallback to jobs
  useEffect(() => {
    const loadSkills = async () => {
      try {
        setLoading(true);
        const skillsSet = new Set();
        
        // Method 1: Try public skills endpoint (returns all skills from skill_categories)
        try {
          const response = await employerAPI.getAllSkills();
          
          // New response structure: { categories: [...], skills: [...] }
          // Extract skills from the flat skills array
          const skillsData = response.data?.skills || [];
          
          if (Array.isArray(skillsData) && skillsData.length > 0) {
            skillsData.forEach(skill => {
              const skillName = typeof skill === 'string' ? skill : (skill.name || skill.skill_name);
              if (skillName && typeof skillName === 'string') {
                skillsSet.add(skillName.trim());
              }
            });
            console.log(`✅ Loaded ${skillsData.length} skills from skill_categories`);
            
            // Also extract skills from categories if needed (backup)
            if (response.data?.categories && Array.isArray(response.data.categories)) {
              response.data.categories.forEach(category => {
                if (category.skills && Array.isArray(category.skills)) {
                  category.skills.forEach(skill => {
                    const skillName = typeof skill === 'string' ? skill : (skill.name || skill.skill_name);
                    if (skillName && typeof skillName === 'string') {
                      skillsSet.add(skillName.trim());
                    }
                  });
                }
              });
            }
            
            // If we got skills from the endpoint, use them and skip other methods
            const skillsList = Array.from(skillsSet).sort();
            setAllSkills(skillsList);
            console.log(`✅ Total: ${skillsList.length} unique skills from all categories`);
            setLoading(false);
            return;
          }
        } catch (publicError) {
          console.log('Skills endpoint not available, trying fallback methods...', publicError);
        }
        
        // Method 2: Fallback - Get skills from existing job postings (employers can see their own jobs)
        try {
          const jobsResponse = await employerAPI.getJobPostings();
          const jobs = jobsResponse.data?.jobs || jobsResponse.data || [];
          
          jobs.forEach(job => {
            const jobSkills = job.requiredSkills || job.required_skills || [];
            if (Array.isArray(jobSkills)) {
              jobSkills.forEach(skill => {
                if (skill && typeof skill === 'string') {
                  skillsSet.add(skill.trim());
                }
              });
            } else if (typeof jobSkills === 'string') {
              jobSkills.split(',').forEach(skill => {
                const trimmed = skill.trim();
                if (trimmed) skillsSet.add(trimmed);
              });
            }
          });
          
          console.log(`📋 Found ${skillsSet.size} unique skills from existing jobs`);
        } catch (jobsError) {
          console.log('Could not load skills from jobs:', jobsError);
        }
        
        // Convert to sorted array
        const skillsList = Array.from(skillsSet).sort();
        
        if (skillsList.length > 0) {
          setAllSkills(skillsList);
          console.log(`✅ Total: ${skillsList.length} skills available for selection`);
        } else {
          console.log('⚠️ No skills found. Manual entry will be available.');
        }
      } catch (error) {
        console.error('Error loading skills:', error);
        // If all fails, allow manual entry (empty skills list)
      } finally {
        setLoading(false);
      }
    };
    
    loadSkills();
  }, []);

  // Filter skills based on search term
  const filteredSkills = allSkills.filter(skill =>
    skill.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleSkill = (skill) => {
    const isSelected = selectedSkills.some(s => s.toLowerCase() === skill.toLowerCase());
    let newSkills;
    
    if (isSelected) {
      newSkills = selectedSkills.filter(s => s.toLowerCase() !== skill.toLowerCase());
    } else {
      newSkills = [...selectedSkills, skill];
    }
    
    onChange(newSkills.join(', '));
  };

  const handleRemoveSkill = (skillToRemove) => {
    const newSkills = selectedSkills.filter(skill => skill !== skillToRemove);
    onChange(newSkills.join(', '));
  };

  return (
    <div>
      <label className="form-label">
        Required Skills {required && <span className="text-red-500">*</span>}
      </label>
      
      {/* Selected Skills Tags */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 p-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[40px]">
          {selectedSkills.map((skill, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
            >
              {skill}
              <button
                type="button"
                onClick={() => handleRemoveSkill(skill)}
                className="ml-1 text-primary-600 hover:text-primary-800 focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown Button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full input-field text-left flex items-center justify-between ${error ? 'border-red-500' : ''}`}
        >
          <span className={selectedSkills.length === 0 ? 'text-gray-400' : ''}>
            {selectedSkills.length === 0 
              ? 'Click to select skills from the system' 
              : `${selectedSkills.length} skill${selectedSkills.length !== 1 ? 's' : ''} selected`}
          </span>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search skills..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
            </div>

            {/* Skills List */}
            <div className="overflow-y-auto max-h-64">
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  <svg className="animate-spin h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-2 text-sm">Loading skills...</p>
                </div>
              ) : filteredSkills.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? (
                    <div>
                      <p>No skills found matching your search</p>
                    </div>
                  ) : allSkills.length === 0 ? (
                    <div>
                      <p>No skills available from system</p>
                      <p className="text-xs mt-1">You can type skills manually below</p>
                    </div>
                  ) : (
                    'No skills available'
                  )}
                </div>
              ) : (
                <div className="p-2">
                  {filteredSkills.map((skill, index) => {
                    const isSelected = selectedSkills.some(s => s.toLowerCase() === skill.toLowerCase());
                    return (
                      <label
                        key={index}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSkill(skill)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">{skill}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <p className="mt-1 text-xs text-gray-500">
        {allSkills.length > 0 
          ? 'Select skills from the system to ensure better matching with candidates'
          : 'Skills list not available. You can type skills manually below (comma-separated)'}
      </p>
      
      {/* Fallback: Manual textarea if no skills loaded or as alternative */}
      {allSkills.length === 0 && (
        <textarea
          rows={3}
          className={`input-field mt-2 ${error ? 'border-red-500' : ''}`}
          placeholder="Enter skills separated by commas (e.g., JavaScript, React, Node.js, AWS)"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error.message}</p>
      )}
    </div>
  );
};

// Helper function to get Indian IT cities
const getIndianITCities = () => {
  return [
    'Bangalore',
    'Hyderabad',
    'Pune',
    'Chennai',
    'Mumbai',
    'Delhi',
    'Noida',
    'Gurgaon',
    'Kolkata',
    'Ahmedabad',
    'Jaipur',
    'Chandigarh',
    'Indore',
    'Coimbatore',
    'Kochi',
    'Trivandrum',
    'Bhubaneswar',
    'Vadodara',
    'Nagpur',
    'Mysore',
    'Remote'
  ];
};

// Location Multi-Select Component
const LocationMultiSelect = ({ selectedLocations, setSelectedLocations, otherLocation, setOtherLocation, error, setValue, register }) => {
  const cities = getIndianITCities();
  const hasOther = selectedLocations.includes('Other');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = React.useRef(null);

  // Filter cities based on search term
  const filteredCities = cities.filter(city =>
    city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationToggle = (city) => {
    let updated;
    if (selectedLocations.includes(city)) {
      updated = selectedLocations.filter(loc => loc !== city);
      if (city === 'Other') {
        setOtherLocation('');
      }
    } else {
      updated = [...selectedLocations, city];
    }
    setSelectedLocations(updated);
    
    // Update form value for validation
    let locationString = '';
    if (updated.length > 0) {
      const locationParts = updated.map(loc => {
        if (loc === 'Other' && otherLocation.trim()) {
          return `Other: ${otherLocation.trim()}`;
        }
        return loc;
      }).filter(loc => loc !== 'Other' || otherLocation.trim());
      locationString = locationParts.join(', ');
    }
    setValue('location', locationString, { shouldValidate: true });
  };

  const handleOtherLocationChange = (value) => {
    setOtherLocation(value);
    
    // Update form value
    let locationString = '';
    if (selectedLocations.length > 0) {
      const locationParts = selectedLocations.map(loc => {
        if (loc === 'Other' && value.trim()) {
          return `Other: ${value.trim()}`;
        }
        return loc;
      }).filter(loc => loc !== 'Other' || value.trim());
      locationString = locationParts.join(', ');
    }
    setValue('location', locationString, { shouldValidate: true });
  };

  const handleRemoveLocation = (locationToRemove) => {
    handleLocationToggle(locationToRemove);
  };

  return (
    <div>
      <label className="form-label">
        Location <span className="text-red-500">*</span>
      </label>
      
      {/* Selected Locations Display */}
      {selectedLocations.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 p-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[40px]">
          {selectedLocations.map((location, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
            >
              {location === 'Other' && otherLocation ? `Other: ${otherLocation}` : location}
              <button
                type="button"
                onClick={() => handleRemoveLocation(location)}
                className="ml-1 text-primary-600 hover:text-primary-800 focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown Button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`w-full input-field text-left flex items-center justify-between ${error ? 'border-red-500' : ''}`}
        >
          <span className={selectedLocations.length === 0 ? 'text-gray-400' : ''}>
            {selectedLocations.length === 0 
              ? 'Select location(s)...' 
              : `${selectedLocations.length} location${selectedLocations.length !== 1 ? 's' : ''} selected`}
          </span>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsDropdownOpen(false)}
            ></div>
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
              {/* Search Input */}
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search locations..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>

              {/* Locations List */}
              <div className="overflow-y-auto max-h-64">
                <div className="p-2">
                  {filteredCities.map((city) => {
                    const isSelected = selectedLocations.includes(city);
                    return (
                      <label
                        key={city}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleLocationToggle(city)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">{city}</span>
                      </label>
                    );
                  })}
                  
                  {/* Other Option */}
                  <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded">
                    <input
                      type="checkbox"
                      checked={hasOther}
                      onChange={() => handleLocationToggle('Other')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">Other</span>
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Other Location Text Input */}
      {hasOther && (
        <div className="mt-3">
          <label htmlFor="otherLocation" className="block text-sm font-medium text-gray-700 mb-1">
            Specify Other Location <span className="text-red-500">*</span>
          </label>
          <input
            id="otherLocation"
            type="text"
            value={otherLocation}
            onChange={(e) => handleOtherLocationChange(e.target.value)}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              hasOther && !otherLocation.trim() ? 'border-red-500' : ''
            }`}
            placeholder="Enter location name..."
          />
          {hasOther && !otherLocation.trim() && (
            <p className="mt-1 text-sm text-red-600">Please specify the location</p>
          )}
        </div>
      )}

      {/* Hidden input for form validation */}
      <input
        type="hidden"
        {...register('location', {
          required: 'Please select at least one location',
          validate: () => {
            if (selectedLocations.length === 0) {
              return 'Please select at least one location';
            }
            if (hasOther && !otherLocation.trim()) {
              return 'Please specify the location for "Other" option';
            }
            return true;
          }
        })}
      />

      {/* Validation Error */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error.message}</p>
      )}
    </div>
  );
};

export default JobPosting;

